import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { BTN, BTN_ON, TILE } from '../ui/platformStandard';
import '../../styles/breakerPanel.css';
import ModalSurface from '../shared/ModalSurface';
import {
    AI_CEILING_HELP, AI_CEILING_LABEL, AI_CHOICES, DOOR_LABEL, PANEL_TABS, TEST_CONTACT_KINDS, pauseAllAi, resumeAllAi, setAi,
    addTestContact, searchClientAccounts, loadClientAccountNames, looksLikeId, companyNameMatches, deleteTestRecords, discardOutbox, everythingToTest, freshAuthenticatorStep,
    friendlyBreakerError, goLive, goTest, loadHistory, loadKeyPresence, loadOutbox, loadPanel, loadPreflight, loadTestList,
    recordWalkthrough, releaseOutbox, removeTestContact, setTestContactAiTester, TEST_REASONS, testReasonText,
    WALKTHROUGH_STATUS_LABEL,
} from '../../lib/breakerPanel';
import { refreshModuleModes } from '../../hooks/useModuleModes';

// Breaker Panel G3 — Admin Center › Go-Live Panel (Owners and Tenant Admins; platform admins too).
// One breaker per module: TEST (works fully inside the app; outside doors open only to the test list) or LIVE.
// Going Live is Owner-only and needs a fresh authenticator code, the company name typed, and a Passed walkthrough or a written
// override (flagged red). Going back to Test is one tap plus a reason pick (default "Emergency"; nothing to type).
// Inside-only modules (nothing leaves the app) show a grey "Inside only" label and no switch. Everything else here is read
// from the database.

const KEY_LABEL = { stripe: 'Stripe', stripe_tax: 'Stripe Tax', twilio: 'Twilio', mapbox: 'Mapbox', flightaware: 'FlightAware', certn: 'Certn' };
const when = (value) => (value ? new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '');

function ModeBadge({ mode }) {
    const live = mode === 'live';
    return (
        <span className={`ia-mode-badge ${live ? 'ia-mode-badge--live' : 'ia-mode-badge--test'}`} data-testid="mode-badge">
            <span className="ia-mode-badge__dot" aria-hidden="true" />{live ? 'LIVE' : 'TEST'}
        </span>
    );
}

function Check({ ok, advisory = false, label, detail }) {
    const state = ok ? 'ok' : advisory ? 'info' : 'no';
    return (
        <li className="ia-golive-check">
            <span className={`ia-golive-check__mark ia-golive-check__mark--${state}`} aria-hidden="true">{ok ? '✓' : advisory ? '!' : '✕'}</span>
            <span><span className="font-semibold">{label}</span><span className="block text-[var(--ink-muted)]">{detail}</span></span>
            <span className="sr-only">{ok ? 'passed' : advisory ? 'note' : 'not passed'}</span>
        </li>
    );
}

/** The confirm step for Go Live and for Delete TEST records: authenticator code + company name (+ walkthrough/override). */
export function ConfirmDialog({ kind, module, panel, client, auth, onClose, onDone }) {
    const [preflight, setPreflight] = useState(null);
    const [keys, setKeys] = useState(null);
    const [walkthrough, setWalkthrough] = useState('');
    const [override, setOverride] = useState('');
    const [company, setCompany] = useState('');
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const live = kind === 'live';

    useEffect(() => {
        if (!live) return undefined;
        let active = true;
        loadPreflight(client, panel.org_id, module.module_key).then((data) => { if (active) setPreflight(data); }).catch(() => {});
        loadKeyPresence(client, panel.org_id).then((data) => { if (active) setKeys(data); }).catch(() => { if (active) setKeys({}); });
        return () => { active = false; };
    }, [client, live, module.module_key, panel.org_id]);

    const passed = (module.walkthroughs || []).filter((w) => w.status === 'passed');
    const needsOverride = live && !walkthrough;
    const canSubmit = companyNameMatches(company, panel.company_names) && code.replace(/\D/g, '').length === 6
        && (!live || walkthrough || override.trim()) && !busy;

    const submit = async (event) => {
        event.preventDefault();
        setBusy(true);
        setError('');
        try {
            await freshAuthenticatorStep(auth, code);
            const result = live
                ? await goLive(client, { orgId: panel.org_id, moduleKey: module.module_key, companyName: company, walkthroughRef: walkthrough || null, overrideReason: walkthrough ? null : override.trim() })
                : await deleteTestRecords(client, { orgId: panel.org_id, moduleKey: module.module_key, companyName: company });
            onDone(live ? `${module.label} is now LIVE.` : `Deleted ${result?.deleted ?? 0} TEST record(s)${result?.kept ? `; kept ${result.kept} still in use` : ''}.`);
        } catch (err) {
            setError(friendlyBreakerError(err));
        } finally {
            setBusy(false);
        }
    };

    const serverKeys = preflight?.server_keys || [];
    const names = panel.company_names || [];
    return (
        <ModalSurface label="Go-Live confirmation" onClose={onClose} closeOnEscape={false} closeDisabled={busy} labelledBy="golive-dialog-title"
            overlayClassName="ia-golive-dialog-backdrop ia-golive-scope" panelClassName={`${TILE} ia-golive-dialog`}>
            <form className="ia-golive-dialog__form ia-modal-layout" onSubmit={submit}>
                <div className="ia-modal-body ia-golive-dialog__body">
                <h3 id="golive-dialog-title" className="text-xl font-extrabold text-[var(--ink)]">
                    {live ? `Switch ${module.label} to LIVE` : `Delete ${module.label} TEST records`}
                </h3>
                <p className="text-sm text-[var(--ink-muted)]">
                    {live
                        ? 'Live means real clients, drivers and money. Texts, emails, payments and links reach everyone, not just the test list.'
                        : `This deletes the ${module.test_records} TEST record(s) in ${module.label}. Live records are never touched.`}
                </p>
                {live && (
                    <section aria-label="Pre-flight checklist">
                        <h4 className="mb-1 text-sm font-bold uppercase tracking-wide text-[var(--ink-muted)]">Pre-flight</h4>
                        <ul className="ia-golive-list">
                            {(preflight?.checks || []).map((check) => <Check key={check.key} {...check} />)}
                            {serverKeys.map((key) => {
                                const k = keys?.[key];
                                const detail = !keys ? 'Checking…' : !k ? 'Could not check' : k.present
                                    ? (k.mode ? `Set (${k.mode === 'test' ? 'test key' : 'live key'})` : 'Set') : 'Not set';
                                return <Check key={`key-${key}`} ok={Boolean(k?.present)} label={`${KEY_LABEL[key] || key} key present`} detail={detail} />;
                            })}
                            {!preflight && <li className="ia-golive-check text-[var(--ink-muted)]">Checking…</li>}
                        </ul>
                    </section>
                )}
                {live && (
                    <fieldset className="ia-golive-field">
                        <legend className="font-semibold">Passed walkthrough</legend>
                        {passed.length === 0 && <span className="text-sm text-[var(--ink-muted)]">No Passed walkthrough recorded for {module.label} yet. Record one on its tile, or write an override below.</span>}
                        <div className="ia-pill-row" role="radiogroup" aria-label="Passed walkthrough">
                            {passed.map((w) => (
                                <button key={w.id} type="button" role="radio" aria-checked={walkthrough === w.id} className="ia-hollow-pill"
                                    onClick={() => setWalkthrough(walkthrough === w.id ? '' : w.id)}>
                                    Passed {when(w.run_at)}{w.recorded_by_name ? ` · ${w.recorded_by_name}` : ''}
                                </button>
                            ))}
                        </div>
                    </fieldset>
                )}
                {needsOverride && (
                    <label className="ia-golive-field">
                        <span className="ia-override-flag">⚠ Override — going Live without a Passed walkthrough</span>
                        <textarea className="ia-override" rows={2} value={override} onChange={(e) => setOverride(e.target.value)}
                            placeholder="Why is it safe to go Live without a walkthrough?" />
                    </label>
                )}
                <label className="ia-golive-field">
                    <span className="font-semibold">Type the company name to confirm</span>
                    <input value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="off" aria-describedby="golive-name-hint" />
                    <span id="golive-name-hint" className="text-xs text-[var(--ink-muted)]">{names.length ? `Type ${names.join(' or ')}` : ''}</span>
                </label>
                <label className="ia-golive-field">
                    <span className="font-semibold">Code from your authenticator app</span>
                    <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={8} placeholder="6-digit code" />
                </label>
                {error && <p role="alert" className="text-sm font-semibold text-red-600 dark:text-red-300">{error}</p>}
                </div>
                <div className="ia-golive-actions ia-modal-actions">
                    <button type="button" className={BTN} onClick={onClose}>Cancel</button>
                    {live
                        ? <button type="submit" className={BTN_ON} disabled={!canSubmit}>{busy ? 'Switching…' : 'Go Live'}</button>
                        : <button type="submit" className="ia-danger-btn" disabled={!canSubmit}>{busy ? 'Deleting…' : 'Delete TEST records'}</button>}
                </div>
            </form>
        </ModalSurface>
    );
}

/** Back to Test: one tap. The reason is a pick (default "Emergency") with an optional note — nothing needs typing. */
function ReasonForm({ label, button, danger = false, onSubmit, onCancel }) {
    const [pick, setPick] = useState(TEST_REASONS[0]);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    return (
        <form className="flex flex-col gap-2" onSubmit={async (e) => {
            e.preventDefault(); setBusy(true); setError('');
            try { await onSubmit(testReasonText(pick, note)); } catch (err) { setError(friendlyBreakerError(err)); setBusy(false); }
        }}>
            <fieldset className="ia-golive-field">
                <legend className="font-semibold">{label}</legend>
                <div className="ia-pill-row" role="radiogroup" aria-label="Reason">
                    {TEST_REASONS.map((r) => (
                        <button key={r} type="button" role="radio" aria-checked={pick === r} className="ia-hollow-pill" onClick={() => setPick(r)}>{r}</button>
                    ))}
                </div>
            </fieldset>
            <label className="ia-golive-field">
                <span className="text-sm text-[var(--ink-muted)]">Note (optional)</span>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything to add" />
            </label>
            {error && <p role="alert" className="text-sm font-semibold text-red-600 dark:text-red-300">{error}</p>}
            <div className="ia-golive-actions">
                <button type="button" className={BTN} onClick={onCancel}>Cancel</button>
                <button type="submit" className={danger ? 'ia-danger-btn' : BTN_ON} disabled={busy}>{busy ? 'Switching…' : button}</button>
            </div>
        </form>
    );
}

/** Owner / Tenant Admin: record the result of an end-to-end walkthrough of this module in Test. */
function WalkthroughForm({ module, panel, client, onDone, onCancel }) {
    const [status, setStatus] = useState('passed');
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    return (
        <form className="flex flex-col gap-2" onSubmit={async (e) => {
            e.preventDefault(); setBusy(true); setError('');
            try {
                await recordWalkthrough(client, { orgId: panel.org_id, moduleKey: module.module_key, status, notes: notes.trim() });
                await onDone(`Walkthrough recorded for ${module.label}: ${WALKTHROUGH_STATUS_LABEL[status]}.`);
            } catch (err) { setError(friendlyBreakerError(err)); setBusy(false); }
        }}>
            <fieldset className="ia-golive-field">
                <legend className="font-semibold">Walkthrough result for {module.label}</legend>
                <div className="ia-pill-row" role="radiogroup" aria-label="Result">
                    {Object.entries(WALKTHROUGH_STATUS_LABEL).map(([key, text]) => (
                        <button key={key} type="button" role="radio" aria-checked={status === key} className="ia-hollow-pill" onClick={() => setStatus(key)}>{text}</button>
                    ))}
                </div>
            </fieldset>
            <label className="ia-golive-field">
                <span className="text-sm text-[var(--ink-muted)]">Notes (optional)</span>
                <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was tested" />
            </label>
            {error && <p role="alert" className="text-sm font-semibold text-red-600 dark:text-red-300">{error}</p>}
            <div className="ia-golive-actions">
                <button type="button" className={BTN} onClick={onCancel}>Cancel</button>
                <button type="submit" className={BTN_ON} disabled={busy}>{busy ? 'Saving…' : 'Save result'}</button>
            </div>
        </form>
    );
}

/** Breaker Panel G5: the AI switch for one module, under its fixed ceiling. Never more live than the module. */
export function AiSwitch({ module, panel, client, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    if (module.ai_ceiling === 'none') return null;
    const paused = module.module_key === 'comms' && panel.attendant?.paused_all;
    const run = async (fn) => { setBusy(true); setError(''); try { await fn(); await onChanged(); } catch (err) { setError(friendlyBreakerError(err)); } finally { setBusy(false); } };
    return (
        <div className="flex flex-col gap-2" data-testid={`ai-switch-${module.module_key}`}>
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--ink-muted)]">AI switch · {AI_CEILING_LABEL[module.ai_ceiling].replace(/^AI /, "")}</span>
            <div className="ia-pill-row" role="group" aria-label={`${module.label} AI`}>
                {AI_CHOICES.map((choice) => (
                    <button key={choice.key} type="button" className="ia-hollow-pill" aria-pressed={module.ai_mode === choice.key}
                        disabled={!panel.can_go_test || busy || (choice.key === 'on' && !panel.is_owner)}
                        title={choice.key === 'on' && !panel.is_owner ? 'Only the Owner can turn AI On' : undefined}
                        onClick={() => run(() => setAi(client, { orgId: panel.org_id, moduleKey: module.module_key, aiMode: choice.key }))}>
                        {choice.label}
                    </button>
                ))}
                {module.module_key === 'comms' && panel.can_go_test && (
                    <button type="button" className={paused ? BTN_ON : 'ia-danger-btn'} disabled={busy}
                        onClick={() => run(() => (paused ? resumeAllAi(client, panel.org_id) : pauseAllAi(client, panel.org_id)))}>
                        {paused ? 'Resume AI' : 'Pause all AI'}
                    </button>
                )}
            </div>
            <span className="text-xs text-[var(--ink-muted)]">
                {AI_CEILING_HELP[module.ai_ceiling]}
                {panel.can_go_test && !panel.is_owner ? ' Only the Owner can turn AI On.' : ''}
                {module.ai_mode === 'on' && module.mode !== 'live' ? ` Works as Test while ${module.label} is in Test.` : ''}
                {paused ? ' All AI is paused right now.' : ''}
                {module.module_key === 'comms' && panel.attendant?.status === 'pause' ? ' The Attendant is paused in its own settings.' : ''}
            </span>
            {error && <p role="alert" className="text-sm font-semibold text-red-600 dark:text-red-300">{error}</p>}
        </div>
    );
}

function ModuleTile({ module, panel, onGoLive, onGoTest, onDelete, client, onChanged }) {
    const [askReason, setAskReason] = useState(false);
    const [recording, setRecording] = useState(false);
    const live = module.mode === 'live';
    const inside = !module.has_outside_doors;
    const lastWalk = (module.walkthroughs || [])[0];
    if (inside) {
        // Inside-only modules have no breaker: nothing leaves the app, so there is nothing to switch (fix round 1, finding 5).
        return (
            <article className={`${TILE} ia-golive-tile ia-golive-tile--inside`} aria-label={`${module.label}: Inside only`} data-module={module.module_key}>
                <div className="ia-golive-tile__head">
                    <h3 className="ia-golive-tile__name">{module.label}</h3>
                    <span className="ia-mode-badge ia-mode-badge--inside" data-testid="inside-only">Inside only</span>
                </div>
                <div className="ia-golive-meta"><span>Nothing leaves the app — always on, no Live/Test switch.</span></div>
                <div className="ia-golive-meta">
                    <span>{module.ai_ceiling === 'none' ? 'No AI' : `AI working now: ${module.ai_effective === 'off' ? 'Off' : module.ai_effective === 'test' ? 'Test' : 'On'}`}</span>
                </div>
                <AiSwitch module={module} panel={panel} client={client} onChanged={onChanged} />
            </article>
        );
    }
    return (
        <article className={`${TILE} ia-golive-tile ${module.override ? 'ia-override' : ''}`} aria-label={`${module.label}: ${live ? 'Live' : 'Test'}`} data-module={module.module_key}>
            <div className="ia-golive-tile__head">
                <h3 className="ia-golive-tile__name">{module.label}</h3>
                <ModeBadge mode={module.mode} />
            </div>
            <div className="ia-golive-meta">
                <span>{(module.doors || []).map((d) => DOOR_LABEL[d]).join(' · ')}</span>
            </div>
            {module.module_key === 'comms' && (
                <p className="ia-golive-note" data-testid="comms-pipe-note">Texts/emails pipe gate arrives after the port (G6).</p>
            )}
            <div className="ia-golive-meta">
                <span>{module.ai_ceiling === 'none' ? 'No AI' : `AI working now: ${module.ai_effective === 'off' ? 'Off' : module.ai_effective === 'test' ? 'Test' : 'On'}`}</span>
                {(module.depends_on || []).length > 0 && <span>Needs: {module.depends_on.join(', ')}</span>}
                {module.outbox_caught + module.outbox_held > 0 && <span>Test Outbox: {module.outbox_caught} caught{module.outbox_held ? `, ${module.outbox_held} held` : ''}</span>}
                {module.test_records > 0 && <span>{module.test_records} TEST record(s)</span>}
                <span>{lastWalk ? `Walkthrough: ${WALKTHROUGH_STATUS_LABEL[lastWalk.status]} ${when(lastWalk.run_at)}` : 'No walkthrough recorded'}</span>
            </div>
            {module.override && <span className="ia-override-flag">⚠ Live by override: {module.reason}</span>}
            <AiSwitch module={module} panel={panel} client={client} onChanged={onChanged} />
            {module.changed_at && (
                <p className="text-xs text-[var(--ink-muted)]">
                    {live ? 'Live' : 'Test'} since {when(module.changed_at)}{module.changed_by_name ? ` · ${module.changed_by_name}` : ''}
                    {module.walkthrough_ref && live ? ` · walkthrough ${module.walkthrough_ref}` : ''}
                </p>
            )}
            {askReason ? (
                <ReasonForm label={`Why is ${module.label} going back to Test?`} button="Switch to Test"
                    onCancel={() => setAskReason(false)} onSubmit={async (reason) => { await onGoTest(module, reason); setAskReason(false); }} />
            ) : recording ? (
                <WalkthroughForm module={module} panel={panel} client={client} onCancel={() => setRecording(false)}
                    onDone={async (message) => { setRecording(false); await onChanged(message); }} />
            ) : (
                <div className="ia-golive-actions">
                    {!live && panel.is_owner && <button type="button" className={BTN_ON} onClick={() => onGoLive(module)}>Go Live…</button>}
                    {!live && !panel.is_owner && <span className="text-xs text-[var(--ink-muted)]">Only the Owner can switch a module to Live.</span>}
                    {live && panel.can_go_test && <button type="button" className={BTN} onClick={() => setAskReason(true)}>Switch to Test</button>}
                    {!live && panel.can_go_test && <button type="button" className={BTN} onClick={() => setRecording(true)}>Record walkthrough…</button>}
                    {panel.is_owner && module.test_records > 0 && ['dispatch', 'events', 'schedule', 'invoices', 'payments'].includes(module.module_key) && (
                        <button type="button" className={BTN} onClick={() => onDelete(module)}>Delete TEST records…</button>
                    )}
                </div>
            )}
        </article>
    );
}

function TestListTab({ client, orgId, canEdit }) {
    const [rows, setRows] = useState(null);
    const [names, setNames] = useState({});
    const [kind, setKind] = useState('phone');
    const [value, setValue] = useState('');
    const [label, setLabel] = useState('');
    const [picked, setPicked] = useState(null);
    const [matches, setMatches] = useState([]);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState('');
    const reload = useCallback(() => loadTestList(client, orgId).then(async (list) => {
        setRows(list);
        const ids = list.filter((row) => row.kind === 'client_account').map((row) => row.value);
        try { setNames(await loadClientAccountNames(client, orgId, ids)); } catch { setNames({}); }
    }).catch((e) => setError(friendlyBreakerError(e))), [client, orgId]);
    useEffect(() => { reload(); }, [reload]);
    const spec = TEST_CONTACT_KINDS.find((k) => k.key === kind);
    const accountMode = kind === 'client_account';
    // Client accounts: search by name (2+ letters). A pasted account id still works.
    useEffect(() => {
        if (!accountMode || picked || looksLikeId(value) || value.trim().length < 2) { setMatches([]); setSearching(false); return undefined; }
        let active = true; setSearching(true);
        const timer = setTimeout(() => {
            searchClientAccounts(client, orgId, value)
                .then((found) => { if (active) setMatches(found); })
                .catch((e) => { if (active) { setMatches([]); setError(friendlyBreakerError(e)); } })
                .finally(() => { if (active) setSearching(false); });
        }, 250);
        return () => { active = false; clearTimeout(timer); };
    }, [accountMode, picked, value, client, orgId]);
    const chooseKind = (key) => { setKind(key); setValue(''); setPicked(null); setMatches([]); };
    const submitValue = accountMode ? (picked?.id || (looksLikeId(value) ? value.trim() : '')) : value.trim();
    return (
        <section aria-label="Test list" className="flex flex-col gap-4">
            <p className="text-sm text-[var(--ink-muted)]">While a module is in Test, its texts, emails, push alerts, job offers and client pages reach only the people and accounts on this list. Phones and emails marked <strong>AI tester</strong> are also the AI Attendant's test contacts (the two lists stay in step).</p>
            {canEdit && (
                <form className="flex flex-wrap items-end gap-3" onSubmit={async (e) => {
                    e.preventDefault(); setError('');
                    if (!submitValue) return;
                    try {
                        await addTestContact(client, { orgId, kind, value: submitValue, label: label || (accountMode ? picked?.account_name : '') });
                        setValue(''); setLabel(''); setPicked(null); setMatches([]); reload();
                    } catch (err) { setError(friendlyBreakerError(err)); }
                }}>
                    <div className="ia-pill-row" role="tablist" aria-label="Kind">
                        {TEST_CONTACT_KINDS.map((k) => (
                            <button key={k.key} type="button" role="tab" aria-selected={kind === k.key} className="ia-hollow-pill" onClick={() => chooseKind(k.key)}>{k.label}</button>
                        ))}
                    </div>
                    <div className="ia-golive-field ia-golive-search min-w-[220px] flex-1">
                        <label className="flex flex-col gap-1"><span className="font-semibold">{spec.label}</span>
                            <input value={picked ? picked.account_name : value} placeholder={spec.placeholder} aria-autocomplete={accountMode ? 'list' : undefined}
                                aria-controls={accountMode ? 'golive-account-matches' : undefined}
                                onChange={(e) => { setPicked(null); setValue(e.target.value); }} /></label>
                        {accountMode && picked && <span className="text-xs text-[var(--ink-muted)]">Selected: {picked.account_name} <button type="button" className="ia-golive-linkbtn" onClick={() => { setPicked(null); setValue(''); }}>Change</button></span>}
                        {accountMode && !picked && searching && <span className="text-xs text-[var(--ink-muted)]" role="status">Searching…</span>}
                        {accountMode && !picked && !searching && value.trim().length >= 2 && !looksLikeId(value) && matches.length === 0 && <span className="text-xs text-[var(--ink-muted)]" role="status">No client accounts match “{value.trim()}”.</span>}
                        {accountMode && !picked && matches.length > 0 && (
                            <ul id="golive-account-matches" className="ia-golive-matches" aria-label="Matching client accounts">
                                {matches.map((m) => (
                                    <li key={m.id}><button type="button" className="ia-golive-match" onClick={() => { setPicked(m); setMatches([]); }}>{m.account_name}</button></li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <label className="ia-golive-field min-w-[160px]"><span className="font-semibold">Label (optional)</span>
                        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={accountMode ? (picked?.account_name || 'Client name') : "Val's phone"} /></label>
                    <button type="submit" className={BTN_ON} disabled={!submitValue}>Add</button>
                </form>
            )}
            {error && <p role="alert" className="text-sm font-semibold text-red-600 dark:text-red-300">{error}</p>}
            <ul className="ia-golive-list">
                {(rows || []).map((row) => {
                    const accountName = row.kind === 'client_account' ? names[row.value] : null;
                    return (
                    <li key={row.id} className="ia-golive-row">
                        <span className="ia-golive-row__main"><span className="font-semibold">{row.label || accountName || row.value}</span>
                            <span className="block text-xs text-[var(--ink-muted)]">{TEST_CONTACT_KINDS.find((k) => k.key === row.kind)?.label} · {accountName && row.label !== accountName ? `${accountName} · ` : ''}{row.value}</span></span>
                        {(row.kind === 'phone' || row.kind === 'email') && (
                            <button type="button" className="ia-hollow-pill" role="switch" aria-checked={Boolean(row.ai_tester)} disabled={!canEdit}
                                onClick={async () => { try { await setTestContactAiTester(client, row.id, !row.ai_tester); reload(); } catch (err) { setError(friendlyBreakerError(err)); } }}>
                                AI tester
                            </button>
                        )}
                        {canEdit && <button type="button" className={BTN} onClick={async () => { try { await removeTestContact(client, row.id); reload(); } catch (err) { setError(friendlyBreakerError(err)); } }}>Remove</button>}
                    </li>
                    );
                })}
                {rows && rows.length === 0 && <li className="ia-golive-row text-[var(--ink-muted)]">The test list is empty.</li>}
            </ul>
        </section>
    );
}

function OutboxTab({ client, orgId, canAct, onChanged }) {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');
    const reload = useCallback(() => loadOutbox(client, orgId).then(setRows).catch((e) => setError(friendlyBreakerError(e))), [client, orgId]);
    useEffect(() => { reload(); }, [reload]);
    const act = async (fn) => { setError(''); try { await fn(); await reload(); onChanged?.(); } catch (err) { setError(friendlyBreakerError(err)); } };
    return (
        <section aria-label="Test Outbox" className="flex flex-col gap-3">
            <p className="text-sm text-[var(--ink-muted)]">What Test stopped from leaving the app. <strong>Caught</strong> items were never sent. <strong>Held</strong> items were waiting to go out when the module went back to Test — release them once it is Live again, or discard them.</p>
            {error && <p role="alert" className="text-sm font-semibold text-red-600 dark:text-red-300">{error}</p>}
            <ul className="ia-golive-list">
                {(rows || []).map((row) => (
                    <li key={row.id} className="ia-golive-row">
                        <span className="ia-golive-row__main">
                            <span className="font-semibold">{row.summary || row.source}</span>
                            <span className="block text-xs text-[var(--ink-muted)]">Would have gone to {row.would_have_gone_to} · {row.module_key} · {DOOR_LABEL[row.door]} · {when(row.created_at)}</span>
                        </span>
                        <span className={`ia-mode-badge ${row.status === 'held' || row.status === 'caught' ? 'ia-mode-badge--test' : 'ia-mode-badge--live'}`}>{row.status.toUpperCase()}</span>
                        {canAct && row.status === 'held' && <button type="button" className={BTN_ON} onClick={() => act(() => releaseOutbox(client, row.id))}>Release</button>}
                        {canAct && (row.status === 'held' || row.status === 'caught') && <button type="button" className={BTN} onClick={() => act(() => discardOutbox(client, row.id))}>Discard</button>}
                    </li>
                ))}
                {rows && rows.length === 0 && <li className="ia-golive-row text-[var(--ink-muted)]">Nothing caught yet.</li>}
            </ul>
        </section>
    );
}

function HistoryTab({ client, orgId }) {
    const [rows, setRows] = useState(null);
    useEffect(() => { loadHistory(client, orgId).then(setRows).catch(() => setRows([])); }, [client, orgId]);
    return (
        <section aria-label="History">
            <ul className="ia-golive-list">
                {(rows || []).map((row) => (
                    <li key={row.id} className={`ia-golive-row ${row.override ? 'ia-override' : ''}`}>
                        <span className="ia-golive-row__main">
                            <span className="font-semibold">{row.module_key}: {row.from_mode ? `${row.from_mode} → ` : ''}{row.to_mode}{row.from_ai_mode !== row.to_ai_mode ? ` · AI ${row.from_ai_mode || '—'} → ${row.to_ai_mode}` : ''}</span>
                            <span className="block text-xs text-[var(--ink-muted)]">{when(row.changed_at)} · {row.source}{row.reason ? ` · ${row.reason}` : ''}{row.walkthrough_ref ? ` · walkthrough ${row.walkthrough_ref}` : ''}</span>
                            {row.override && <span className="ia-override-flag">⚠ Override</span>}
                        </span>
                    </li>
                ))}
                {rows && rows.length === 0 && <li className="ia-golive-row text-[var(--ink-muted)]">No changes yet.</li>}
            </ul>
        </section>
    );
}

export default function GoLivePanel({ client = supabaseClient, initialTab = 'panel' }) {
    const [tab, setTab] = useState(initialTab);
    const [panel, setPanel] = useState(null);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [dialog, setDialog] = useState(null);
    const [askEverything, setAskEverything] = useState(false);

    const reload = useCallback(async () => {
        try {
            const data = await loadPanel(client);
            // Shape-safe: an odd answer shows an empty panel, never a crash.
            setPanel({ ...(data && typeof data === 'object' ? data : {}), modules: Array.isArray(data?.modules) ? data.modules : [],
                company_names: Array.isArray(data?.company_names) ? data.company_names : [], test_list_count: Number(data?.test_list_count) || 0 });
            setError('');
        } catch (err) { setError(friendlyBreakerError(err)); }
    }, [client]);
    useEffect(() => { reload(); }, [reload]);

    const done = useCallback(async (message) => {
        setDialog(null); setAskEverything(false); setNotice(message);
        await reload(); refreshModuleModes(client);
    }, [client, reload]);

    const liveCount = useMemo(() => (panel?.modules || []).filter((m) => m.mode === 'live').length, [panel]);
    const switchable = useMemo(() => (panel?.modules || []).filter((m) => m.has_outside_doors).length, [panel]);

    if (error && !panel) return <div role="alert" className="text-sm font-semibold text-red-600 dark:text-red-300">{error}</div>;
    if (!panel) return <p className="text-[var(--ink-muted)]">Loading the Go-Live Panel…</p>;

    return (
        <div className="ia-golive-scope flex flex-col gap-5" data-testid="go-live-panel">
            <header className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                    <p className="text-sm text-[var(--ink-muted)]">
                        {liveCount} of {switchable} switchable modules Live · test list: {panel.test_list_count} {panel.test_list_count === 1 ? 'entry' : 'entries'}
                    </p>
                    <p className="mt-1 max-w-2xl text-sm text-[var(--ink-muted)]">
                        In <strong>Test</strong> a module works fully inside the app; anything leaving it reaches only the test list, and the rest waits in the Test Outbox.
                        Staff screens, staff alerts, the bell and reports always work.
                    </p>
                </div>
                {panel.can_go_test && !askEverything && liveCount > 0 && (
                    <button type="button" className="ia-danger-btn" onClick={() => setAskEverything(true)}>Everything to Test</button>
                )}
            </header>
            {askEverything && (
                <div className={`${TILE} ia-golive-tile ia-override`}>
                    <ReasonForm label="Switch every module back to Test now? Pick a reason." button="Everything to Test" danger
                        onCancel={() => setAskEverything(false)}
                        onSubmit={async (reason) => { const n = await everythingToTest(client, { orgId: panel.org_id, reason }); await done(`${n} module(s) switched back to Test.`); }} />
                </div>
            )}
            {notice && <p role="status" className="text-sm font-semibold text-[var(--selected-pill-ink)]">{notice}</p>}
            <div className="ia-pill-row" role="tablist" aria-label="Go-Live Panel sections">
                {PANEL_TABS.map((t) => (
                    <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className="ia-hollow-pill" onClick={() => setTab(t.key)}>{t.label}</button>
                ))}
            </div>
            {tab === 'panel' && (
                <div className="ia-golive-grid">
                    {panel.modules.map((module) => (
                        <ModuleTile key={module.module_key} module={module} panel={panel} client={client} onChanged={done}
                            onGoLive={(m) => setDialog({ kind: 'live', module: m })}
                            onDelete={(m) => setDialog({ kind: 'delete', module: m })}
                            onGoTest={async (m, reason) => {
                                const r = await goTest(client, { orgId: panel.org_id, moduleKey: m.module_key, reason });
                                await done(`${m.label} is back in Test.${r?.held ? ` ${r.held} waiting message(s) held in the Test Outbox.` : ''}`);
                            }} />
                    ))}
                </div>
            )}
            {tab === 'test-list' && <TestListTab client={client} orgId={panel.org_id} canEdit={panel.can_go_test} />}
            {tab === 'outbox' && <OutboxTab client={client} orgId={panel.org_id} canAct={panel.can_go_test} onChanged={reload} />}
            {tab === 'history' && <HistoryTab client={client} orgId={panel.org_id} />}
            {dialog && (
                <ConfirmDialog kind={dialog.kind} module={dialog.module} panel={panel} client={client} auth={client.auth}
                    onClose={() => setDialog(null)} onDone={done} />
            )}
        </div>
    );
}
